import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiZap, FiTarget, FiShield, FiTrendingUp, FiBarChart, FiCpu, FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
import './AdvancedAIOps.css';

const AdvancedAIOps = () => {
  const features = [
    {
      icon: <FiZap />,
      title: "AI SRE Suite",
      description: "Complete AI SRE suite for automated incident discovery and resolution."
    },
    {
      icon: <FiTarget />,
      title: "Automated Discovery",
      description: "AI automatically discovers and categorizes incidents across your infrastructure."
    },
    {
      icon: <FiShield />,
      title: "Automated Resolution",
      description: "AI-powered automated resolution for common incidents and issues."
    },
    {
      icon: <FiTrendingUp />,
      title: "Predictive Analytics",
      description: "Predict and prevent incidents before they impact your systems."
    },
    {
      icon: <FiBarChart />,
      title: "Advanced Analytics",
      description: "Comprehensive analytics and insights for SRE operations."
    },
    {
      icon: <FiCpu />,
      title: "Intelligent Automation",
      description: "Smart automation that learns from your environment and improves over time."
    }
  ];

  const benefits = [
    "AI SRE suite for automated incident management",
    "Automated incident discovery and categorization",
    "AI-powered automated resolution",
    "Predictive analytics for incident prevention",
    "Advanced SRE analytics and insights",
    "Intelligent automation that learns and improves"
  ];

  return (
    <div className="advanced-aiops-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>Advanced AIOps</h1>
            <p className="hero-subtitle">
              AI SRE suite for automated incident discovery and resolution
            </p>
            <div className="hero-actions">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/pricing" className="btn btn-primary">
                  Start Free Trial
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/demo" className="btn btn-outline">
                  Request Demo
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Next-Generation AIOps</h2>
            <p>Complete AI SRE suite with automated incident discovery and resolution</p>
          </motion.div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits">
        <div className="container">
          <div className="benefits-content">
            <motion.div 
              className="benefits-text"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>Why Choose Our Advanced AIOps?</h2>
              <p>
                Our Advanced AIOps platform provides a complete AI SRE suite with 
                automated incident discovery and resolution capabilities.
              </p>
              <ul className="benefits-list">
                {benefits.map((benefit, index) => (
                  <motion.li 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <FiCheckCircle />
                    {benefit}
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div 
              className="benefits-visual"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="dashboard-preview">
                <div className="dashboard-header">
                  <div className="dashboard-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <h4>AIOps Dashboard</h4>
                </div>
                <div className="dashboard-content">
                  <div className="metric">
                    <FiAlertTriangle />
                    <span>Incidents: 12</span>
                  </div>
                  <div className="metric">
                    <FiCheckCircle />
                    <span>Resolved: 98%</span>
                  </div>
                  <div className="metric">
                    <FiCpu />
                    <span>MTTR: 2.3min</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready for Advanced AIOps?</h2>
            <p>Experience the power of AI SRE with automated incident discovery and resolution.</p>
            <div className="cta-actions">
              <Link to="/pricing" className="btn btn-primary">
                Start Free Trial
              </Link>
              <Link to="/contact" className="btn btn-outline">
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdvancedAIOps;
